/*
LICENSE (MIT): https://github.com/microsoft/fluentui/blob/master/LICENSE
CODE:
https://github.com/microsoft/fluentui/blob/master/packages/fluentui/react-northstar/src/utils/fontSizeUtility.ts
https://github.com/microsoft/fluentui/blob/master/packages/fluentui/react-northstar/src/utils/isBrowser.tsx

Fluent UI React

Copyright (c) Microsoft Corporation

All rights reserved.

  MIT License

Permission is hereby granted, free of charge, to any person obtaining a copy of this software
and associated documentation files (the ""Software""), to deal in the Software without
restriction, including without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the
Software is furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in all copies or
  substantial portions of the Software.

  THE SOFTWARE IS PROVIDED *AS IS*, WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING
  BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
  NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
  DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

  Note: Usage of the fonts and icons referenced in Fluent UI React is subject to the terms
  listed at https://aka.ms/fluentui-assets-license
*/

// CODE: https://github.com/microsoft/fluentui/blob/master/packages/fluentui/react-northstar/src/utils/isBrowser.tsx
// -----------------------------------------------------------------------------------------------------------------
/* eslint-disable no-undef */
const hasDocument = typeof document === "object" && document !== null;
// @ts-ignore
const hasWindow = typeof window === "object" && window !== null && window.self === window;
/* eslint-enable no-undef */

const isBrowser = () => hasDocument && hasWindow;

// CODE: https://github.com/microsoft/fluentui/blob/master/packages/fluentui/react-northstar/src/utils/fontSizeUtility.ts
// ----------------------------------------------------------------------------------------------------------------------
const DEFAULT_REM_SIZE_IN_PX = 16;

// const isBrowser = true;
let _documentRemSize: number | null = null;

export const getDocumentRemSize = (): number => {
  if (isBrowser()) {
    try {
      // eslint-disable-next-line no-undef
      return (
        getFontSizeValue(getComputedStyle(document.documentElement).fontSize) ||
        DEFAULT_REM_SIZE_IN_PX
      );
    } catch (e) {
      return DEFAULT_REM_SIZE_IN_PX;
    }
  }

  return DEFAULT_REM_SIZE_IN_PX;
};

const getFontSizeValue = (size?: string | null): number | null => {
  return (size && parseFloat(size)) || null;
};

// A replacement for a Lodash's one, way more faster on our cases as handles specific scenario
export const round = (n: number) => ((n * 10000 + (n > 0 ? 0.5 : -0.5)) << 0) / 10000;

/**
 * Converts the provided px size to rem based on the default font size of 16px unless
 * the HTML font size has been previously defined with setHTMLFontSize().
 * @param valueInPx - The px value to convert to rem.
 * @param baseRemSize - Rem size to use for conversions. Optional - document's font size will be taken otherwise.
 * @example
 * ```
 * // Returns '1rem' for default document font size (16px).
 * pxToRem(16)
 *
 * // Returns '2rem'.
 * pxToRem(32, 16)
 * ```
 * @returns The value converted to the rem.
 */
export const pxToRem = (valueInPx: number, baseRemSize?: number): string => {
  if (!baseRemSize && !_documentRemSize) {
    // there is no way how to reset the cached value
    // invalidating the cache is not possible as resetting cached value won't trigger recalculation of site variables,
    // for which originally computed values will stay unchanged
    _documentRemSize = getDocumentRemSize();
  }

  const remSize = baseRemSize || _documentRemSize || DEFAULT_REM_SIZE_IN_PX;
  const convertedValueInRems = valueInPx / remSize;

  return `${round(convertedValueInRems)}rem`;
};
